"use client";

import { ensureConnection, setTransactionContext, getOriginationContext } from "@/services/encompass.service";
import { getOrigin, getToken, CustomError } from "@/services/epc-api-service";
import { useEllimae } from "@/store/useEllimae";
import { useSearchParams } from "next/navigation";
import { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { logAction, logError, updateSessionContext } from "@/services/datadog.service";
import { InitializationError } from "@/components/view/InitializationError";
import { AuditState } from "@/types/epc-interfaces";
const MAX_TOKEN_ATTEMPTS = 3;
const TOKEN_RETRY_DELAY_MS = 1000;
export const isValidJwt = (value: unknown): value is string => typeof value === "string" && value.split(".").length === 3;
export const getTokenExpiry = (token: string): number | null => {
  try {
    const raw = token.split(".")[1];
    const padded = raw.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(raw.length / 4) * 4, "=");
    const exp = JSON.parse(atob(padded)).exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
};
export const isTokenExpired = (token: string, bufferSeconds = 30): boolean => {
  const exp = getTokenExpiry(token);
  if (exp === null) return false;
  return Date.now() / 1000 > exp - bufferSeconds;
};
const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

// Generate a unique session ID for this frontend session
function generateSessionId(): string {
  return `fe_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}
export const WithEllimae: FC<PropsWithChildren> = ({
  children
}) => {
  const searchParam = useSearchParams();
  const initInFlightRef = useRef(false);
  const [initError, setInitError] = useState(false);
  const {
    setEllimaeState,
    initialized,
    isUat,
    isUatChecked,
    setIsUAT,
    setIsUATChecked,
    setLoading
  } = useEllimae(state => state);
  useEffect(() => {
    if (isUatChecked) return;
    if (searchParam.get("isUAT") === "true") {
      setIsUAT(true);
    }
    setIsUATChecked(true);
  }, [isUatChecked, searchParam, setIsUAT, setIsUATChecked]);
  useEffect(() => {
    if (initialized || !isUatChecked || initInFlightRef.current) {
      return;
    }
    initInFlightRef.current = true;
    setLoading(true);
    let isMounted = true;
    let capturedSessionId: string | undefined;
    const initializeEllimaeConnection = async () => {
      if (!(await ensureConnection())) {
        throw new Error("Failed to ensure connection to Encompass");
      }
      const {
        originId,
        partnerAccessToken,
        transactionId
      } = await getOriginationContext();
      logAction("origination_context_loaded", {
        originId,
        hasPartnerAccessToken: !!partnerAccessToken,
        transactionId
      });
      if (!originId || !partnerAccessToken) {
        return;
      }
      const sessionId = generateSessionId();
      capturedSessionId = sessionId;

      // ── Step 1: Obtain a valid token (up to MAX_TOKEN_ATTEMPTS) ──────────────
      let token: string | undefined;
      let tokenAttemptCount = 0;
      for (let attempt = 1; attempt <= MAX_TOKEN_ATTEMPTS; attempt++) {
        tokenAttemptCount = attempt;
        try {
          const candidate = await getToken({
            originId,
            isUat,
            partnerAccessToken,
            sessionId
          });
          if (isValidJwt(candidate)) {
            token = candidate;
            break;
          }
        } catch {
          // swallow and retry
        }
        if (attempt < MAX_TOKEN_ATTEMPTS) {
          await delay(TOKEN_RETRY_DELAY_MS);
        }
      }
      if (!token) {
        logError(new Error("WithEllimae initialization failed after retries"), {
          sessionId,
          failedStep: "getToken",
          attemptCount: tokenAttemptCount
        });
        if (isMounted) {
          setLoading(false);
          setInitError(true);
        }
        return;
      }

      // ── Proactive refresh if token is near expiry ─────────────────────────
      if (isTokenExpired(token)) {
        try {
          const refreshCandidate = await getToken({
            originId,
            isUat,
            partnerAccessToken,
            sessionId
          });
          if (isValidJwt(refreshCandidate) && !isTokenExpired(refreshCandidate)) {
            token = refreshCandidate;
          }
        } catch (refreshError) {
          // swallow — proceed with existing token; 403 retry logic will handle it
          logError(refreshError instanceof Error ? refreshError : new Error("Proactive token refresh failed"), {
            failedStep: "proactiveRefresh"
          });
        }
      }

      // ── Step 2: Call getOrigin; on 403, refresh token once and retry ─────────
      let originResponse;
      let originAttemptCount = 0;
      try {
        originAttemptCount = 1;
        originResponse = await getOrigin({
          originId,
          token,
          partnerAccessToken,
          isUat,
          sessionId
        });
      } catch (err) {
        const is403 = err instanceof CustomError && err.context?.status === 403;
        if (is403) {
          // Refresh token once
          let freshToken: string | undefined;
          try {
            const candidate_0 = await getToken({
              originId,
              isUat,
              partnerAccessToken,
              sessionId
            });
            if (isValidJwt(candidate_0)) {
              freshToken = candidate_0;
            }
          } catch {
            // swallow
          }
          if (freshToken) {
            try {
              originAttemptCount = 2;
              originResponse = await getOrigin({
                originId,
                token: freshToken,
                partnerAccessToken,
                isUat,
                sessionId
              });
              token = freshToken; // update so Step 3 stores the refreshed token in Zustand
            } catch (retryErr) {
              logError(retryErr instanceof Error ? retryErr : new Error("WithEllimae initialization failed after retries"), {
                sessionId,
                failedStep: "getOrigin",
                attemptCount: originAttemptCount
              });
              if (isMounted) {
                setLoading(false);
                setInitError(true);
              }
              return;
            }
          } else {
            logError(new Error("WithEllimae initialization failed after retries"), {
              sessionId,
              failedStep: "getOrigin",
              attemptCount: originAttemptCount
            });
            if (isMounted) {
              setLoading(false);
              setInitError(true);
            }
            return;
          }
        } else {
          throw err;
        }
      }

      // ── Step 3: Success path ──────────────────────────────────────────────────
      updateSessionContext({
        sessionId,
        loanId: originResponse.encompassLoanId || null,
        transactionId: transactionId || originResponse.transactionId || null,
        transactionStateId: originResponse.auditDetail?.transactionStateId || null,
        auditRunId: originResponse.auditDetail?.id || null
      });
      logAction("loan_opened", {
        sessionId,
        loanId: originResponse.encompassLoanId || null,
        clientId: originResponse.clientId || null,
        isUat: isUat,
        hasExistingAudit: !!originResponse.auditDetail,
        auditState: originResponse.auditDetail?.auditState || null,
        existingTransactionId: transactionId || originResponse.transactionId || null,
        transactionStateId: originResponse.auditDetail?.transactionStateId || null,
        auditRunId: originResponse.auditDetail?.id || null
      });
      const txId = transactionId ?? originResponse.transactionId;
      if (txId) {
        try {
          await setTransactionContext(txId);
        } catch (error) {
          console.error("[WithEllimae] setTransactionContext error:", error);
        }
      }
      if (isMounted) {
        setLoading(false);
        setEllimaeState({
          token,
          originResponse,
          openedOnRunningAudit: originResponse?.auditDetail?.auditState === AuditState.Processing,
          transactionId,
          sessionId,
          initialized: true
        });
      }
    };
    void initializeEllimaeConnection().catch(error_0 => {
      console.error("[WithEllimae] initialization failed", error_0);
      logError(error_0 instanceof Error ? error_0 : new Error("WithEllimae initialization failed"), {
        ...(capturedSessionId ? {
          sessionId: capturedSessionId
        } : {}),
        failedStep: "unknown"
      });
      if (isMounted) {
        setLoading(false);
        setInitError(true);
      }
    }).finally(() => {
      initInFlightRef.current = false;
    });
    return () => {
      isMounted = false;
    };
  }, [initialized, isUat, isUatChecked, setEllimaeState, setLoading]);
  if (initError) {
    return <InitializationError />;
  }
  return <>{children}</>;
};